import { useIntercom } from "react-use-intercom";
import { useNavigate } from "react-router-dom";
import { issueScreenMap } from "../../../helpers/issueScreenMap";
import { Card } from "common/Card";
import { toast } from "react-toastify";
import api from "api";
import { useState } from "react";
import { AlertCircleIcon } from "lucide-react";
import { Button, ButtonVariant } from "shared/components/Button";
import { CustomerIssue } from "types/issue";

const intercomMessages: {
  [key: string]: string;
} = {
  "unverified-rv-issue":
    "APP-INBOUND: Hi there, I'm trying to resolve an issue with one or more of my unverified payments. Can you help me?",
  "lease-verification-required":
    "APP-INBOUND: Hi there, I'm trying to resolve issues with my lease details. Can you help me?",
  "close-account":
    "APP-INBOUND: Hi there, I'm about to delete my account but wanted to chat briefly first. Can you help me?",
  "cancel-subscription":
    "APP-INBOUND: Hi there, I'm about to turn off auto-renew for my subscription but wanted to chat briefly first. Can you help me?",
  "verify-identity":
    "APP-INBOUND: Hi there, I'm trying to resolve an issue with my identity verification and my submitted data is already correct. Can you help me?",
  "payment-issue-m2m":
    "APP-INBOUND: Hi there, I'm trying to resolve an issue with one or more of my payments (I'm still month-to-month). Can you help me?"
};

// const onboardingMessage = "IN-APP: I have questions about onboarding.";

export const IssueCard = (props: CustomerIssue) => {
  const navigate = useNavigate();
  const { showNewMessage } = useIntercom();
  const [link, setLink] = useState("");
  const identityUrl = props?.params?.shareable_url;

  function handlePress() {
    if (props.screen === "intercom")
      showNewMessage(intercomMessages[props.slug]);
    else if (identityUrl)
      // Make copy to clipboard
      //   Linking.openURL(identityUrl);
      toast.promise(navigator.clipboard.writeText(identityUrl), {
        pending: "Copying link to clipboard...",
        success: "Link copied to clipboard.",
        error:
          "Something went wrong. Please make sure you don't have any clipboard access blockers enabled."
      });
    else {
      let link = issueScreenMap[props.screen];

      if (props.screen === "BF18LeaseClose" && props.params.case_id)
        link = link.replace(":id", String(props.params.case_id));

      if (link && link !== "/404")
        navigate(
          link +
            "?issueResolution=true&issue=" +
            encodeURIComponent(JSON.stringify(props)),
          {
            state: {
              issueResolution: true,
              issue: JSON.stringify(props)
            }
          }
        );
      else
        showNewMessage(
          "APP-INBOUND: Hi there, I'm trying to resolve an issue. Can you help me? Issue is: " +
            props.title
        );
    }
  }

  if (props.slug === "bank-link-bypassed")
    return (
      <Card className="max-w-[342px] w-full h-[316px] shrink-0 p-4 animate-appear">
        <div className="flex flex-col h-full">
          <div className="h-10 w-10 rounded-full flex items-center bg-danger-50 justify-center mb-4">
            <AlertCircleIcon size="20" className="text-danger-500" />
          </div>
          <h2 className="text-base font-bold text-content-1000 mb-2">
            Link your bank account to start reporting rent payments
          </h2>

          <p className="text-sm text-content-500 font-medium">
            Use the secure link we sent to your email and phone, or copy the
            link below and paste it into your browser.
          </p>

          <div className="flex mt-auto gap-2">
            <Button
              className="w-full"
              onClick={() => {
                api
                  .sendBankLinkUrl({ notify_partner: true })
                  .then(() => {
                    toast.clearWaitingQueue();
                    toast.success("Link successfully sent.", {
                      autoClose: 1500,
                      toastId: "bank-link-resent"
                    });
                  })
                  .catch(() => {
                    toast.clearWaitingQueue();
                    toast.error("Something went wrong. Please try again.", {
                      autoClose: 1500,
                      toastId: "bank-link-resent"
                    });
                  });
              }}
            >
              Resend link
            </Button>

            <Button
              className="w-full"
              onClick={() => {
                if (!link)
                  api
                    .sendBankLinkUrl({ notify_partner: true })
                    .then(({ data }) => data)
                    .then((d) => {
                      setLink(d?.plaid_connect_url);
                      return d?.plaid_connect_url;
                    })
                    .then((l) => {
                      toast.clearWaitingQueue();
                      toast.promise(navigator.clipboard.writeText(l || ""), {
                        pending: "Copying link to clipboard...",
                        success: "Link copied to clipboard.",
                        error:
                          "Something went wrong. Please make sure you don't have any clipboard access blockers enabled."
                      });
                    });
                else
                  toast.promise(navigator.clipboard.writeText(link), {
                    pending: "Copying link to clipboard...",
                    success: "Link copied to clipboard.",
                    error:
                      "Something went wrong. Please make sure you don't have any clipboard access blockers enabled."
                  });
              }}
            >
              Copy link
            </Button>
          </div>
        </div>
      </Card>
    );

  return (
    <Card
      className="max-w-[342px] w-full h-[316px] shrink-0 p-4 animate-appear"
      onClick={() => {
        handlePress();
      }}
    >
      <div className="flex flex-col h-full">
        <div className="h-10 w-10 rounded-full flex items-center bg-danger-50 justify-center mb-4">
          <AlertCircleIcon size="20" className="text-danger-500" />
        </div>
        <h2 className="text-base font-bold text-content-1000 mb-2">
          {props.title}
        </h2>

        <p className="text-sm text-content-500 font-medium">
          {props.description}
        </p>
        <div className="flex mt-auto gap-2">
          <Button
            className="w-full"
            variant={ButtonVariant.PRIMARY}
            onClick={() => {}}
          >
            {props.cta}
          </Button>
        </div>
      </div>
    </Card>
  );
};
